import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border border-primary hover:bg-primary/90 font-mono uppercase tracking-[0.14em] text-[12px]",
        destructive: "bg-destructive text-destructive-foreground border border-destructive hover:bg-destructive/90 font-mono uppercase tracking-[0.14em] text-[12px]",
        outline: "border border-border-strong bg-transparent text-foreground hover:bg-surface-2 hover:border-primary/60 font-mono uppercase tracking-[0.14em] text-[12px]",
        secondary: "bg-surface-2 text-foreground border border-border hover:bg-surface-3 font-mono uppercase tracking-[0.14em] text-[12px]",
        ghost: "text-foreground/80 hover:text-foreground hover:bg-surface-2",
        link: "text-primary underline-offset-4 hover:underline",
        operator: "bg-surface-1 text-foreground border border-border-strong hover:border-primary hover:text-primary font-mono uppercase tracking-[0.18em] text-[11px] before:absolute before:left-2 before:content-['>'] before:text-primary",
        terminal: "bg-transparent text-signal-green border border-signal-green/40 hover:bg-signal-green/10 font-mono uppercase tracking-[0.16em] text-[11px]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
